import React, { useState } from 'react';
import { 
  DocumentIcon, 
  EyeIcon, 
  ArrowPathIcon, 
  TrashIcon, 
  CloudArrowUpIcon, 
  PlusIcon 
} from '@heroicons/react/24/outline';
import { Application } from '../../../types';
import { uploadFile } from '../../../services/firebaseService';

interface Props {
  application: Application;
  onOpenDocument: (url: string) => void;
  isEditing?: boolean;
  onUpdateDocument?: (docKey: string, fileUrl: string) => void;
  onUpdateSportsCertificates?: (certUrls: string[]) => void;
}

const DocumentSection: React.FC<Props> = ({ 
  application, 
  onOpenDocument,
  isEditing = false,
  onUpdateDocument,
  onUpdateSportsCertificates
}) => {
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size: 2MB for photo, 20MB for masterAsset, 5MB for certificates
    const maxSize = key === 'masterAsset' ? 20 : (key === 'photo' ? 2 : 5);
    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > maxSize) {
      alert(`File size exceeds limit. Maximum allowed size is ${maxSize}MB.`);
      return;
    }

    setUploadingKey(key);

    try {
      // Upload using secure backend proxy to Cloudinary
      const url = await uploadFile(file, `applications/${application.id}/${key}`);
      
      if (key === 'sportsCertificates') {
        const currentCerts = application.documents?.sportsCertificates || [];
        const updatedCerts = [...currentCerts, url];
        onUpdateSportsCertificates?.(updatedCerts);
      } else {
        onUpdateDocument?.(key, url);
      }
      alert('Document uploaded and updated successfully!');
    } catch (err: any) {
      console.error('Error uploading document:', err);
      alert(`Upload failed: ${err.message || 'Unknown error'}`);
    } finally {
      setUploadingKey(null);
      // Clear file input value to allow uploading the same file again if needed
      e.target.value = '';
    }
  };

  const handleDeleteSportsCertificate = (index: number) => {
    const currentCerts = application.documents?.sportsCertificates || [];
    const updatedCerts = currentCerts.filter((_, i) => i !== index);
    onUpdateSportsCertificates?.(updatedCerts);
  };

  const renderDocSlot = (key: string, label: string, accept: string) => {
    const url = (application.documents as any)?.[key];
    const isUploading = uploadingKey === key;
    const fileInputId = `upload-${key}`;

    if (!url && !isEditing) return null;

    return (
      <div key={key} className="border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">{label}</h4>
          {!url && <p className="text-xs text-gray-500 mb-3 italic">Not yet uploaded</p>}
        </div>
        
        {url ? (
          <div className="space-y-2 mt-auto">
            <button
              onClick={() => onOpenDocument(url)}
              className="w-full bg-primary-50 hover:bg-primary-100 text-primary-700 px-4 py-2 rounded-lg text-sm flex items-center justify-center space-x-2 transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
              <span>View</span>
            </button>
            {isEditing && (
              <div>
                <input
                  type="file"
                  id={fileInputId}
                  accept={accept}
                  onChange={(e) => handleFileChange(e, key)}
                  className="hidden"
                />
                <button
                  onClick={() => document.getElementById(fileInputId)?.click()}
                  disabled={isUploading}
                  className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-4 py-2 rounded-lg text-sm flex items-center justify-center space-x-2 transition-colors border border-indigo-200 disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4" />
                      <span>Replace File</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-auto">
            <input
              type="file"
              id={fileInputId}
              accept={accept}
              onChange={(e) => handleFileChange(e, key)}
              className="hidden"
            />
            <button
              onClick={() => document.getElementById(fileInputId)?.click()}
              disabled={isUploading}
              className="w-full bg-gray-50 hover:bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm flex items-center justify-center space-x-2 transition-colors border border-gray-300 border-dashed disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="w-4 h-4" />
                  <span>Upload File</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="lg:col-span-3">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DocumentIcon className="w-5 h-5 mr-2 text-primary-600" />
          Documents
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Photo */}
          {renderDocSlot('photo', 'Photo', 'image/jpeg,image/png,image/jpg')}

          {/* Master Asset */}
          {(application.documents?.masterAsset || isEditing) && (
            <div className="border border-gray-200 rounded-lg p-4 col-span-1 md:col-span-2 lg:col-span-3 flex flex-col justify-between">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Master Asset (All Documents)</h4>
                {!application.documents?.masterAsset && <p className="text-xs text-gray-500 mb-3 italic">Not yet uploaded</p>}
              </div>
              
              {application.documents?.masterAsset ? (
                <div className="space-y-2 mt-auto">
                  <button
                    onClick={() => onOpenDocument(application.documents?.masterAsset!)}
                    className="w-full bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-4 py-2 rounded-lg text-sm flex items-center justify-center space-x-2 transition-colors border border-yellow-200"
                  >
                    <DocumentIcon className="w-5 h-5" />
                    <span>View Consolidated PDF</span>
                  </button>
                  {isEditing && (
                    <div>
                      <input
                        type="file"
                        id="upload-masterAsset"
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={(e) => handleFileChange(e, 'masterAsset')}
                        className="hidden"
                      />
                      <button
                        onClick={() => document.getElementById('upload-masterAsset')?.click()}
                        disabled={uploadingKey === 'masterAsset'}
                        className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-4 py-2 rounded-lg text-sm flex items-center justify-center space-x-2 transition-colors border border-indigo-200 disabled:opacity-50"
                      >
                        {uploadingKey === 'masterAsset' ? (
                          <>
                            <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="w-4 h-4" />
                            <span>Replace Consolidated PDF</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-auto">
                  <input
                    type="file"
                    id="upload-masterAsset"
                    accept="application/pdf,image/jpeg,image/png"
                    onChange={(e) => handleFileChange(e, 'masterAsset')}
                    className="hidden"
                  />
                  <button
                    onClick={() => document.getElementById('upload-masterAsset')?.click()}
                    disabled={uploadingKey === 'masterAsset'}
                    className="w-full bg-gray-50 hover:bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm flex items-center justify-center space-x-2 transition-colors border border-gray-300 border-dashed disabled:opacity-50"
                  >
                    {uploadingKey === 'masterAsset' ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="w-4 h-4" />
                        <span>Upload Consolidated PDF</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Individual Documents */}
          {[
            { key: 'class10Certificate', label: '10th Certificate', accept: 'image/jpeg,image/png,image/jpg,application/pdf' },
            { key: 'class12Certificate', label: '12th Certificate', accept: 'image/jpeg,image/png,image/jpg,application/pdf' },
            { key: 'aadhaarCard', label: 'Aadhaar Card', accept: 'image/jpeg,image/png,image/jpg,application/pdf' },
            { key: 'casteCertificate', label: 'Caste Certificate', accept: 'image/jpeg,image/png,image/jpg,application/pdf' },
            { key: 'degreeCertificate', label: 'Degree Certificate', accept: 'image/jpeg,image/png,image/jpg,application/pdf' }
          ].map(doc => renderDocSlot(doc.key, doc.label, doc.accept))}

          {/* Sports Certificates */}
          <div className="border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Sports Certificates</h4>
              {application.documents?.sportsCertificates && application.documents.sportsCertificates.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {application.documents.sportsCertificates.map((cert, index) => (
                    <div key={index} className="flex items-center space-x-1">
                      <button
                        onClick={() => onOpenDocument(cert)}
                        className="flex-grow bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1.5 rounded text-xs flex items-center justify-center space-x-1 transition-colors min-w-0 truncate"
                      >
                        <EyeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">Cert {index + 1}</span>
                      </button>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => handleDeleteSportsCertificate(index)}
                          className="bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded transition-colors flex-shrink-0"
                          title="Delete Certificate"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !isEditing && <p className="text-gray-500 text-sm italic mb-2">No certificates uploaded</p>
              )}
            </div>
            
            {isEditing && (
              <div className="mt-auto pt-2 border-t border-gray-100">
                <input
                  type="file"
                  id="upload-sportsCertificates"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  onChange={(e) => handleFileChange(e, 'sportsCertificates')}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('upload-sportsCertificates')?.click()}
                  disabled={uploadingKey === 'sportsCertificates'}
                  className="w-full bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1.5 rounded text-xs flex items-center justify-center space-x-1 transition-colors border border-green-200 border-dashed disabled:opacity-50"
                >
                  {uploadingKey === 'sportsCertificates' ? (
                    <>
                      <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-3.5 h-3.5" />
                      <span>Add Certificate</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSection;
